/**
 * LoveAll Local-First Store
 * All player data lives in localStorage. The backend only receives
 * anonymized telemetry pings keyed by a stable shadow UUID.
 *
 * 2026-05-19: writes now also fan-out to the backend via lib/syncLayer.js
 * (fire-and-forget, never blocks the UI).
 *
 * 2026-05-20: XSS hardening — names are sanitized at write time, JSON
 * blobs are parsed with shape validation, and matches are normalized
 * before storage/sync.
 */
import { sanitizeName, safeParseJSON, sanitizeMatchRecord } from './sanitize';

// NOTE: dynamic import below to avoid a circular dep — syncLayer.js imports getShadowId.
const _sync = async (fnName, ...args) => {
  try {
    const mod = await import('./syncLayer');
    if (typeof mod[fnName] === 'function') mod[fnName](...args);
  } catch { /* sync layer optional */ }
};

const KEYS = {
  SHADOW_ID: 'loveall:shadow_id',
  USER_NAME: 'loveall:user_name',
  MATCHES: 'loveall:matches',
  RIVALS: 'loveall:rivals',
  STREAK: 'loveall:streak',
  ELO: 'loveall:elo',
  UMPIRE: 'loveall:umpire',
  THEME: 'loveall-theme',
  AD_CONFIG_CACHE: 'loveall:ad_cache',
};

// ------- shadow id (anonymous) -------
const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const getShadowId = () => {
  let id = localStorage.getItem(KEYS.SHADOW_ID);
  if (!id) {
    id = uuid();
    localStorage.setItem(KEYS.SHADOW_ID, id);
  }
  return id;
};

// ------- name cache (smart default for Side A) -------
export const getCachedName = () => sanitizeName(localStorage.getItem(KEYS.USER_NAME) || '');
export const setCachedName = (name) => {
  const clean = sanitizeName(name);
  if (clean) {
    localStorage.setItem(KEYS.USER_NAME, clean);
    _sync('syncState', { name: clean });
  }
};

// ------- matches log -------
export const getMatches = () => {
  const list = safeParseJSON(localStorage.getItem(KEYS.MATCHES), 'array', []);
  // Lightly sanitize each entry; skip any that fail validation.
  return list.map(sanitizeMatchRecord).filter(Boolean);
};

export const saveMatch = (match) => {
  const clean = sanitizeMatchRecord(match);
  if (!clean || !clean.id) return null;
  const list = getMatches();
  list.unshift(clean);
  // Cap at 500
  if (list.length > 500) list.length = 500;
  localStorage.setItem(KEYS.MATCHES, JSON.stringify(list));
  // Async DB sync — non-blocking
  _sync('syncMatch', clean);
  _sync('syncState', {
    name: getCachedName(),
    matches_summary: { total: list.length, latest_id: clean.id },
  });
  return clean;
};

// ------- ELO -------
const DEFAULT_ELO = 1200;
const K_FACTOR = 24;

export const getElo = (name) => {
  const map = safeParseJSON(localStorage.getItem(KEYS.ELO), 'object', {});
  const key = sanitizeName(name).toLowerCase();
  return map[key] || DEFAULT_ELO;
};

export const updateElo = (winner, loser) => {
  try {
    const map = safeParseJSON(localStorage.getItem(KEYS.ELO), 'object', {});
    const wKey = sanitizeName(winner).toLowerCase();
    const lKey = sanitizeName(loser).toLowerCase();
    if (!wKey || !lKey) return null;
    const wElo = map[wKey] || DEFAULT_ELO;
    const lElo = map[lKey] || DEFAULT_ELO;
    const expectedW = 1 / (1 + Math.pow(10, (lElo - wElo) / 400));
    const expectedL = 1 - expectedW;
    map[wKey] = Math.round(wElo + K_FACTOR * (1 - expectedW));
    map[lKey] = Math.round(lElo + K_FACTOR * (0 - expectedL));
    localStorage.setItem(KEYS.ELO, JSON.stringify(map));
    // Async DB sync — debounced
    _sync('syncState', { name: getCachedName(), elo: map });
    return { winnerElo: map[wKey], loserElo: map[lKey], wDelta: map[wKey] - wElo, lDelta: map[lKey] - lElo };
  } catch { return null; }
};

export const getAllElo = () => safeParseJSON(localStorage.getItem(KEYS.ELO), 'object', {});

// ------- rivals (H2H) -------
export const getRivals = () => {
  const matches = getMatches();
  const userName = (getCachedName() || '').toLowerCase();
  if (!userName) return [];
  const tally = {};
  matches.forEach(m => {
    const sides = [
      { name: m.sideA?.toLowerCase(), score: m.scoreA, won: m.winner === 'A' },
      { name: m.sideB?.toLowerCase(), score: m.scoreB, won: m.winner === 'B' },
    ];
    const me = sides.find(s => s.name === userName);
    const opp = sides.find(s => s.name !== userName);
    if (!me || !opp || !opp.name) return;
    if (!tally[opp.name]) tally[opp.name] = { name: opp.name, displayName: opp.name === m.sideA?.toLowerCase() ? m.sideA : m.sideB, played: 0, wins: 0, losses: 0, lastPlayed: null };
    tally[opp.name].played += 1;
    if (me.won) tally[opp.name].wins += 1; else tally[opp.name].losses += 1;
    if (!tally[opp.name].lastPlayed || new Date(m.completedAt) > new Date(tally[opp.name].lastPlayed)) {
      tally[opp.name].lastPlayed = m.completedAt;
    }
  });
  return Object.values(tally).sort((a, b) => b.played - a.played);
};

// ------- streak (cardio weekly pattern) -------
const getDow = (d) => new Date(d).getDay(); // 0=Sun, 6=Sat
const isoDate = (d) => new Date(d).toISOString().slice(0, 10);
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Find dominant day-of-week pattern from recent matches.
 * Returns { dow, count, weeksInARow, frozen, dayName }.
 */
export const computeStreak = () => {
  const matches = getMatches();
  if (!matches.length) return { dow: null, weeksInARow: 0, frozen: false, dayName: null, lastPlayed: null };

  // Count matches per day-of-week (last 12 weeks)
  const cutoff = Date.now() - 12 * 7 * 24 * 3600 * 1000;
  const dowCount = [0, 0, 0, 0, 0, 0, 0];
  matches.forEach(m => {
    const t = new Date(m.completedAt).getTime();
    if (t >= cutoff) dowCount[getDow(m.completedAt)] += 1;
  });
  const dominantDow = dowCount.indexOf(Math.max(...dowCount));
  if (dowCount[dominantDow] < 2) {
    return { dow: null, weeksInARow: 0, frozen: false, dayName: null, lastPlayed: matches[0]?.completedAt };
  }

  // Count consecutive weeks where a match was played on dominantDow
  const playedDates = new Set(
    matches
      .filter(m => getDow(m.completedAt) === dominantDow)
      .map(m => isoDate(m.completedAt))
  );

  // Get most recent occurrence of dominantDow
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let cursor = new Date(today);
  while (cursor.getDay() !== dominantDow) cursor.setDate(cursor.getDate() - 1);

  let weeks = 0;
  while (playedDates.has(isoDate(cursor))) {
    weeks += 1;
    cursor.setDate(cursor.getDate() - 7);
  }

  // Frozen: most recent dominantDow date has passed without a match
  let mostRecent = new Date(today);
  while (mostRecent.getDay() !== dominantDow) mostRecent.setDate(mostRecent.getDate() - 1);
  const frozen = weeks > 0 && !playedDates.has(isoDate(mostRecent));

  return {
    dow: dominantDow,
    dayName: DAY_NAMES[dominantDow],
    weeksInARow: weeks,
    frozen,
    lastPlayed: matches[0]?.completedAt,
  };
};

/** Active win-streak for a given player */
export const computeWinStreak = (playerName) => {
  if (!playerName) return 0;
  const matches = getMatches();
  const lc = playerName.toLowerCase();
  let streak = 0;
  for (const m of matches) {
    const aMatch = m.sideA?.toLowerCase() === lc;
    const bMatch = m.sideB?.toLowerCase() === lc;
    if (!aMatch && !bMatch) continue;
    const won = (aMatch && m.winner === 'A') || (bMatch && m.winner === 'B');
    if (won) streak += 1; else break;
  }
  return streak;
};

// ------- umpire badges -------
export const getUmpire = () => {
  const raw = safeParseJSON(localStorage.getItem(KEYS.UMPIRE), 'object', null);
  if (!raw) return { matchesUmpired: 0, level: 0 };
  return {
    matchesUmpired: Number.isFinite(raw.matchesUmpired) ? Math.max(0, Math.min(99999, raw.matchesUmpired)) : 0,
    level: Number.isFinite(raw.level) ? Math.max(0, Math.min(10, raw.level)) : 0,
  };
};

const UMPIRE_LEVELS = [
  { level: 0, label: 'Spectator', threshold: 0 },
  { level: 1, label: 'Apprentice Ref', threshold: 1 },
  { level: 2, label: 'Vetted Referee: Level 2', threshold: 5 },
  { level: 3, label: 'Vetted Referee: Level 3', threshold: 15 },
  { level: 4, label: 'Pro Umpire', threshold: 30 },
  { level: 5, label: 'Master Umpire', threshold: 60 },
];

export const incrementUmpire = () => {
  const cur = getUmpire();
  const next = cur.matchesUmpired + 1;
  let level = 0;
  for (const tier of UMPIRE_LEVELS) {
    if (next >= tier.threshold) level = tier.level;
  }
  const data = { matchesUmpired: next, level };
  localStorage.setItem(KEYS.UMPIRE, JSON.stringify(data));
  // Async DB sync
  _sync('syncState', { name: getCachedName(), umpire: { ...data, label: UMPIRE_LEVELS[level].label } });
  return { ...data, label: UMPIRE_LEVELS[level].label };
};

export const getUmpireLabel = () => {
  const cur = getUmpire();
  return UMPIRE_LEVELS[cur.level]?.label || 'Spectator';
};

// ------- helpers -------
export const estimateCalories = (durationSec) => {
  // ~7 kcal/min for moderate badminton
  return Math.round((durationSec / 60) * 7);
};

export const clearAllLocal = () => {
  Object.values(KEYS).forEach(k => localStorage.removeItem(k));
};

export const exportData = () => ({
  shadowId: localStorage.getItem(KEYS.SHADOW_ID),
  userName: getCachedName(),
  matches: getMatches(),
  elo: getAllElo(),
  umpire: getUmpire(),
});
